/**
 * The decision policy for preparing a still image for upload, parameterised on
 * its effects so it can be pinned in Node (scripts/verify/verify-upload-prepare).
 *
 * Order matters, and the order is the whole point:
 *
 *   1. decode the ORIGINAL file first. Safari decodes HEIC natively and every
 *      normal JPEG decodes everywhere, so for almost every photograph the heavy
 *      WASM converter is never imported at all and the original bytes are what
 *      the host receives.
 *   2. only when native decoding is impossible AND the file is HEIC — Chrome
 *      and Firefox have no HEIC decoder — convert to JPEG, then decode the
 *      converted copy for its derivative.
 *   3. otherwise give up on the derivative, but never on the upload: zero
 *      dimensions and a null thumbnail are exactly what the ticket validator
 *      and `upload-complete` accept, so a photo that cannot be decoded still
 *      reaches the host.
 *
 * The real bindings live in web/src/lib/derivatives.ts: `decodeImage`
 * (createImageBitmap with an <img> fallback), `normaliseHeic` (the dynamic
 * heic2any import) and `renderThumb` (canvas scaling).
 */

export interface PreparedUploadImage {
  /** The file to upload: the original, or the JPEG conversion of a HEIC. */
  readonly file: File;
  readonly width: number;
  readonly height: number;
  /** The small derivative for wall/gallery/queue, or null when none could be made. */
  readonly thumb: Blob | null;
}

export interface ImagePrepareDeps<Source> {
  /**
   * Decode a file's pixels. Returning null means "this browser has no decoder
   * for it"; throwing is treated the same way, because the policy's job is to
   * fall through, not to investigate.
   */
  decode(file: File): Promise<Source | null>;
  /**
   * One HEIC file in. Returns the JPEG conversion, or the SAME file object
   * when conversion was impossible (`normaliseHeic`'s existing contract).
   */
  convertHeic(file: File): Promise<File>;
  /** Render the small derivative from a decoded source, or return null. */
  thumb(source: Source): Promise<Blob | null>;
  /** Report the decoded dimensions. */
  dimensions(source: Source): { width: number; height: number };
}

export async function prepareImage<Source>(
  input: File,
  isHeic: boolean,
  deps: ImagePrepareDeps<Source>,
): Promise<PreparedUploadImage> {
  let upload = input;

  // 1. The fast path. On Safari this succeeds for HEIC too, and the ~2 MB WASM
  //    decoder never reaches the phone at all.
  const decoded = await deps.decode(input).catch(() => null);
  if (decoded) {
    const { width, height } = deps.dimensions(decoded);
    const thumb = await deps.thumb(decoded);
    return { file: input, width, height, thumb };
  }

  // 2. Conversion is only worth a second decode attempt for HEIC: a JPEG that
  //    cannot be decoded will not decode after being copied either.
  if (isHeic) {
    const converted = await deps.convertHeic(input).catch(() => input);
    upload = converted;
    if (converted !== input) {
      const decodedConverted = await deps.decode(converted).catch(() => null);
      if (decodedConverted) {
        const { width, height } = deps.dimensions(decodedConverted);
        const thumb = await deps.thumb(decodedConverted);
        return { file: converted, width, height, thumb };
      }
    }
  }

  // 3. The derivative was never the product — and the upload is the most
  //    converted file we have. Null thumb and zero dimensions are valid
  //    everywhere downstream.
  return { file: upload, width: 0, height: 0, thumb: null };
}
